import { SHBox, SHStack, SHTypography } from "@components/design-systems";
import { TooltipAllowSVG } from "@components/svgs";
import { Zoom } from "@mui/material";
import shadows from "@mui/material/styles/shadows";
import { useTheme } from "@mui/system";
import { HtmlTooltip } from "@pages/platform-analysis/_id/steps/fee/analysis/components/sub-product-card";
import { hexToRGBA } from "@utils";
import { isEqual } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { useToggle } from "react-use";

type SHTextOverflowTooltipProps = {
  tooltipContent: string | JSX.Element | React.ReactNode;
  children: JSX.Element | string | undefined;
  lineClamp?: number;
  breakAnyWhere?: boolean;
  preventGetFitWidth?: boolean;
  tooltipWidth?: number;
  tooltipPlacement?:
    | "bottom-end"
    | "bottom-start"
    | "bottom"
    | "left-end"
    | "left-start"
    | "left"
    | "right-end"
    | "right-start"
    | "right"
    | "top-end"
    | "top-start"
    | "top";
};

export const SHTextOverflowTooltip = ({
  tooltipContent,
  tooltipWidth,
  tooltipPlacement = "top-start",
  lineClamp = 2,
  breakAnyWhere = false,
  preventGetFitWidth = false,
  children,
}: SHTextOverflowTooltipProps) => {
  const { palette } = useTheme();
  const [isTooltipOpening, openTooltip] = useToggle(false);
  const [isFlipped, toggleFlipped] = useToggle(false);
  const [isClamped, toggleIsClamped] = useToggle(false);
  const [fitWidth, setFitWidth] = useState<"fit-content" | number>(
    "fit-content",
  );

  const contentRef = useRef<HTMLDivElement>();
  const tooltipRef = useRef<HTMLDivElement>();

  const getFitElementWidth = (): "fit-content" | number => {
    if (isClamped || preventGetFitWidth) return "fit-content";

    const childNodes = contentRef.current?.childNodes;

    if (!childNodes) return "fit-content";

    const range = document.createRange();
    const textNode = Array.from(childNodes)?.find((node) => {
      const firstChild = node.firstChild;
      return !!firstChild && firstChild.nodeType === Node.TEXT_NODE;
    });

    if (!textNode) return "fit-content";

    range.selectNodeContents(textNode);
    const rect = range.getBoundingClientRect();
    const fitWidth = Math.ceil(rect.width);

    return !!fitWidth ? fitWidth : "fit-content";
  };

  const calculateClampedState = () => {
    const scrollHeight = contentRef.current?.scrollHeight ?? 0;
    const clientHeight = contentRef.current?.clientHeight ?? 0;
    const scrollWidth = contentRef.current?.scrollWidth ?? 0;
    const clientWidth = contentRef.current?.clientWidth ?? 0;
    return scrollHeight > clientHeight || scrollWidth > clientWidth;
  };

  useEffect(() => {
    toggleIsClamped(calculateClampedState());
    // eslint-disable-next-line
  }, [
    contentRef.current?.scrollHeight,
    contentRef.current?.clientHeight,
    contentRef.current?.scrollWidth,
    contentRef.current?.clientWidth,
  ]);

  useEffect(() => {
    setFitWidth(getFitElementWidth());
    // eslint-disable-next-line
  }, [isClamped]);

  return (
    <HtmlTooltip
      open={isTooltipOpening}
      followCursor
      TransitionComponent={Zoom}
      placement={tooltipPlacement}
      PopperProps={{
        modifiers: [
          {
            name: "onUpdate",
            fn: ({ state }) => {
              const popperStyles = state.styles.popper;
              toggleFlipped(!isEqual(popperStyles.left, "0"));
            },
          },
        ],
      }}
      title={
        <SHStack
          ref={tooltipRef}
          sx={{
            backgroundColor: hexToRGBA(palette.common.white, 0.9),
            border: "1px solid #E3E3E3",
            backdropFilter: "blur(5px)",
            borderRadius: "3px",
            boxShadow: shadows[1],
            position: "relative",
            ml: "-19px",
            p: 1,
            width: tooltipWidth ?? "fit-content",
            left: isFlipped ? "20px" : "0px",
          }}
        >
          <SHTypography variant="body1">{tooltipContent}</SHTypography>
          <SHBox
            sx={{
              position: "absolute",
              bottom: "-11.7px",
              left: isFlipped ? "280px" : "0",
            }}
          >
            <TooltipAllowSVG />
          </SHBox>
        </SHStack>
      }
      onClose={() => openTooltip(false)}
      onOpen={() => {
        const isClamped = calculateClampedState();
        openTooltip(isClamped);
      }}
    >
      <SHStack
        ref={contentRef}
        sx={{
          lineBreak: breakAnyWhere ? "anywhere" : "auto",
          WebkitLineBreak: breakAnyWhere ? "anywhere" : "auto",
          overflow: "hidden",
          display: "-webkit-box",
          lineClamp: lineClamp,
          WebkitLineClamp: lineClamp,
          WebkitBoxOrient: "vertical",
          width: fitWidth,
        }}
      >
        {children}
      </SHStack>
    </HtmlTooltip>
  );
};
